import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Info as InfoIcon,
  HowToReg as LeaveIcon,
  FilterList as FilterIcon,
  AssessmentOutlined as ReportIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import * as actions from '../../redux/actions';
import { showAlert } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { canApproveByCategory } from '../../utils/functions/approverAccess';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import LeaveSummaryModal from './LeaveSummaryModal';

dayjs.locale('th');

const LEAVE_TYPE_LABELS = {
  SICK: 'ลาป่วย',
  PERSONAL: 'ลากิจ',
  VACATION: 'ลาพักร้อน',
  MATERNITY: 'ลาคลอด',
  ORDINATION: 'ลาบวช',
  UNPAID: 'ลาโดยไม่รับค่าจ้าง',
};

const PERIOD_LABELS = {
  FULL_DAY: 'เต็มวัน',
  MORNING: 'ครึ่งวันเช้า',
  AFTERNOON: 'ครึ่งวันบ่าย',
};

const STATUS_CONFIG = {
  PENDING: { label: 'รออนุมัติ', color: 'warning' },
  APPROVED: { label: 'อนุมัติ', color: 'success' },
  REJECTED: { label: 'ไม่อนุมัติ', color: 'error' },
  CANCELLED: { label: 'ยกเลิก', color: 'default' },
};

export default function HrLeaveRequestList({ title }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const leaveRequest = useSelector((state) => state.leaveRequest);
  const canApprove = canApproveByCategory(me, info, 'leave_approver', '/humen/leave-request');

  const [filterStatus, setFilterStatus] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const approverId = me?.userData?._id;

  const fetchAll = () => {
    dispatch(actions.leaveRequestAll({ status: filterStatus }));
  };

  useEffect(() => {
    fetchAll();
  }, [filterStatus]);

  useEffect(() => {
    dispatch(actions.getInformation({}));
  }, []);

  const handleApprove = async () => {
    if (!selected) return;
    setProcessing(true);
    try {
      await dispatch(actions.leaveRequestApprove(selected._id, approverId, note));
      setApproveOpen(false);
      setDetailOpen(false);
      setNote('');
      setSelected(null);
      fetchAll();
    } catch {
      showAlert('เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!note.trim()) {
      showAlert('กรุณาระบุเหตุผลในการไม่อนุมัติ');
      return;
    }
    setProcessing(true);
    try {
      await dispatch(actions.leaveRequestReject(selected._id, approverId, note));
      setRejectOpen(false);
      setDetailOpen(false);
      setNote('');
      setSelected(null);
      fetchAll();
    } catch {
      showAlert('เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setProcessing(false);
    }
  };

  const rows = leaveRequest?.rows || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <LeaveIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{title || 'คำขอลา (HR)'}</Typography>
        <Box flexGrow={1} />
        <Button
          variant="outlined"
          startIcon={<ReportIcon />}
          onClick={() => setReportOpen(true)}
        >
          ดูรายงาน
        </Button>
      </Box>

      {/* ฟิลเตอร์ */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FilterIcon color="action" fontSize="small" />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>กรองตามสถานะ</InputLabel>
              <Select
                value={filterStatus}
                label="กรองตามสถานะ"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">ทั้งหมด</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                  <MenuItem key={val} value={val}>{cfg.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              รายการทั้งหมด: <b>{rows.length}</b> รายการ
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {leaveRequest?.isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box py={6} textAlign="center">
              <LeaveIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">ไม่มีรายการขอลา</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>รหัส</TableCell>
                    <TableCell>พนักงาน</TableCell>
                    <TableCell>แผนก / ตำแหน่ง</TableCell>
                    <TableCell>ประเภทการลา</TableCell>
                    <TableCell>วันที่ลา</TableCell>
                    <TableCell align="center">จำนวนวัน</TableCell>
                    <TableCell align="center">สถานะ</TableCell>
                    <TableCell>วันที่ยื่น</TableCell>
                    <TableCell align="center">จัดการ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const emp = row.employee || {};
                    const statusCfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.PENDING;
                    return (
                      <TableRow key={row._id} hover>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">{row.code || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <EmployeeAvatar employee={emp} size={28} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {formatEmployeeThaiName(emp)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {emp.employee_id || ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{emp.department?.name || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{emp.role?.name || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={LEAVE_TYPE_LABELS[row.leave_type] || row.leave_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(row.start_date).format('DD/MM/YY')}
                            {' — '}
                            {dayjs(row.end_date).format('DD/MM/YY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {PERIOD_LABELS[row.period]}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={700}>{row.total_days}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {dayjs(row.request_date || row.createdAt).format('DD/MM/YY')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<InfoIcon />}
                              onClick={() => { setSelected(row); setDetailOpen(true); }}
                              sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              ดูรายละเอียด
                            </Button>
                            {row.status === 'PENDING' && canApprove && (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<ApproveIcon />}
                                  onClick={() => { setSelected(row); setNote(''); setApproveOpen(true); }}
                                  sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                >
                                  อนุมัติ
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<RejectIcon />}
                                  onClick={() => { setSelected(row); setNote(''); setRejectOpen(true); }}
                                  sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                >
                                  ไม่อนุมัติ
                                </Button>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog รายละเอียด */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>รายละเอียดคำขอลา</DialogTitle>
        <DialogContent>
          {selected && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {(() => {
                const emp = selected.employee || {};
                return (
                  <>
                    <Grid item xs={12}>
                      <Box bgcolor="primary.50" borderRadius={1} p={1.5} mb={1}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {formatEmployeeThaiName(emp)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {emp.employee_id} | {emp.department?.name} | {emp.role?.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">รหัสคำขอ</Typography>
                      <Typography fontFamily="monospace">{selected.code}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">สถานะ</Typography>
                      <Box><Chip label={STATUS_CONFIG[selected.status]?.label} color={STATUS_CONFIG[selected.status]?.color} size="small" /></Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">ประเภทการลา</Typography>
                      <Typography>{LEAVE_TYPE_LABELS[selected.leave_type]}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">ช่วงเวลา</Typography>
                      <Typography>{PERIOD_LABELS[selected.period]}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">วันที่เริ่มลา</Typography>
                      <Typography>{dayjs(selected.start_date).format('DD/MM/YYYY')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">วันที่สิ้นสุดลา</Typography>
                      <Typography>{dayjs(selected.end_date).format('DD/MM/YYYY')}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">จำนวนวันลา</Typography>
                      <Typography fontWeight={700} variant="h6">{selected.total_days} วัน</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">เหตุผล</Typography>
                      <Typography>{selected.reason}</Typography>
                    </Grid>
                    {selected.approval_note && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">หมายเหตุจากผู้อนุมัติ</Typography>
                        <Typography>{selected.approval_note}</Typography>
                      </Grid>
                    )}
                    {selected.approver && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">ผู้อนุมัติ</Typography>
                        <Typography>
                          {formatEmployeeThaiName(selected.approver)}
                        </Typography>
                      </Grid>
                    )}
                  </>
                );
              })()}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setDetailOpen(false)} color="inherit" size="small">ปิด</Button>
          {selected?.status === 'PENDING' && canApprove && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RejectIcon />}
                onClick={() => { setNote(''); setRejectOpen(true); }}
              >
                ไม่อนุมัติ
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<ApproveIcon />}
                onClick={() => { setNote(''); setApproveOpen(true); }}
              >
                อนุมัติคำขอลา
              </Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog อนุมัติ */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'success.main' }}>ยืนยันอนุมัติการลา</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Typography gutterBottom>
                อนุมัติคำขอลา <b>{LEAVE_TYPE_LABELS[selected.leave_type]}</b>{' '}
                จำนวน <b>{selected.total_days} วัน</b>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                พนักงาน:{' '}
                {formatEmployeeThaiName(selected.employee)}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="หมายเหตุ (ไม่บังคับ)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={processing}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={14} /> : <ApproveIcon />}
          >
            อนุมัติ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog ไม่อนุมัติ */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>ไม่อนุมัติการลา</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Typography gutterBottom>
                ไม่อนุมัติคำขอลา <b>{LEAVE_TYPE_LABELS[selected.leave_type]}</b>{' '}
                จำนวน <b>{selected.total_days} วัน</b>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                พนักงาน:{' '}
                {formatEmployeeThaiName(selected.employee)}
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                rows={2}
                label="เหตุผลที่ไม่อนุมัติ *"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={processing}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={14} /> : <RejectIcon />}
          >
            ไม่อนุมัติ
          </Button>
        </DialogActions>
      </Dialog>

      <LeaveSummaryModal open={reportOpen} onClose={() => setReportOpen(false)} />
    </Box>
  );
}
